import { useSuspenseQuery } from '@tanstack/react-query';
import {
  OpeningDetailResponse,
  SourcingService,
  VonqContractPostingRequirements,
} from 'api-clients/monolith';
import _range from 'lodash/range';
import { Suspense, useEffect, useMemo, useState } from 'react';

import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { JobDetailFormState } from '../types';
import { JobCard } from './job-card';
import { RefreshBanner } from './refresh-banner';
import { useValidateBaseFields } from './hooks/use-validate-base-fields';
import { SummaryView } from './summary-view';

/**
 * Fields on `JobDetailFormState` that are managed by monolith — refreshed on every
 * successful refetch so the user sees the latest values after editing in monolith.
 * Everything else (`title`, `description`, `stateCode`, `isExpanded`) is user-managed
 * and preserved across refetches.
 */
function serverManagedFields(
  opening: OpeningDetailResponse,
): Pick<
  JobDetailFormState,
  | 'remote'
  | 'industry'
  | 'jobHours'
  | 'jobType'
  | 'experience'
  | 'locationAddress'
  | 'postalCode'
  | 'city'
> {
  return {
    remote: opening.remote,
    industry: opening.job_industry,
    jobHours: opening.job_hours,
    jobType: opening.job_type,
    experience: opening.years_of_experience,
    locationAddress: opening.remote ? 'remote' : opening.location_address,
    postalCode: opening.postal_code,
    city: opening.city,
  };
}

function JobList() {
  const { selectedOpeningIds, hookForm } = useJobPostingContext();
  const { setValue, getValues, watch } = hookForm;
  const validateBaseFields = useValidateBaseFields();
  const jobDetailsList = watch('jobDetails');
  const channels = watch('channels');
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);

  // Only fetch job details for openings that are actually selected in at least
  // one channel's `openingBudgets`. Openings the user removed from every
  // channel are dropped here so we don't waste a request on them.
  const filteredSelectedOpeningIds = useMemo(() => {
    const openingIdsInChannels = new Set<string>();
    for (const channel of Object.values(channels ?? {})) {
      for (const openingId of Object.keys(channel.openingBudgets ?? {})) {
        openingIdsInChannels.add(openingId);
      }
    }
    return selectedOpeningIds.filter(id => openingIdsInChannels.has(id));
  }, [selectedOpeningIds, channels]);

  const {
    data: { openings },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.jobPosting.jobDetails, filteredSelectedOpeningIds],
    queryFn: () =>
      SourcingService.postInternalApiSourcingOpeningsOpeningsByExternalIds({
        opening_external_ids: filteredSelectedOpeningIds,
      }),
  });

  // Collect contract IDs from all Vonq channels for the posting requirements query.
  const contractIds = useMemo(() => {
    const ids: string[] = [];
    for (const channel of Object.values(channels ?? {})) {
      if (channel.vonqDetails?.contractId) {
        ids.push(channel.vonqDetails.contractId);
      }
    }
    return ids;
  }, [channels]);

  // Call the API only when there are contract IDs to avoid unnecessary requests.
  const { data: postingRequirementsData } = useSuspenseQuery({
    queryKey: [...queryKeys.jobPosting.postingRequirements, contractIds],
    queryFn: () =>
      contractIds.length > 0
        ? SourcingService.getInternalApiSourcingVonqContractsPostingRequirements(
            contractIds,
          )
        : Promise.resolve([] as VonqContractPostingRequirements[]),
  });

  /**
   * Reconcile the `jobDetails` form state with the latest `openings` payload
   * whenever it changes (initial mount and every successful refetch).
   * Also initialises `vonqPostingRequirements` for any contract/field pairs
   * that don't yet have a value, preserving any user edits already made.
   */
  useEffect(() => {
    if (openings.length === 0) return;

    const existingJobDetails = getValues('jobDetails');

    const next = openings.reduce<Record<string, JobDetailFormState>>(
      (acc, opening) => {
        const existing = existingJobDetails?.[opening.external_id];
        const serverFields = serverManagedFields(opening);

        // Determine which contracts apply to this opening (i.e. the opening
        // appears in the channel's openingBudgets).
        const applicableContractIds = Object.values(channels ?? {})
          .filter(
            ch =>
              ch.vonqDetails?.contractId &&
              opening.external_id in (ch.openingBudgets ?? {}),
          )
          .map(ch => ch.vonqDetails?.contractId as string);

        // Build / merge vonqPostingRequirements: preserve existing values,
        // fill in empty strings for any new fields.
        const existingRequirements = existing?.vonqPostingRequirements ?? {};
        const mergedRequirements = { ...existingRequirements };

        for (const contractId of applicableContractIds) {
          const contractFields =
            postingRequirementsData?.find(r => r.contract_id === contractId)
              ?.posting_requirements ?? [];

          const existingContractValues = mergedRequirements[contractId] ?? {};
          const merged = { ...existingContractValues };
          for (const field of contractFields) {
            if (!(field.name in merged)) {
              merged[field.name] = field.type === 'MULTIPLE' ? [] : '';
            }
          }
          mergedRequirements[contractId] = merged;
        }

        // Definitions for this opening's applicable contracts — stored in
        // form state so the Zod superRefine can validate required fields
        // without needing any reactive state outside of RHF.
        const vonqPostingRequirementsDefs = (
          postingRequirementsData ?? []
        ).filter(r => applicableContractIds.includes(r.contract_id));

        const base: JobDetailFormState = existing
          ? {
              ...existing,
              ...serverFields,
              vonqPostingRequirements: mergedRequirements,
              vonqPostingRequirementsDefs,
            }
          : {
              ...serverFields,
              slug: opening.slug,
              title: opening.title,
              description: opening.job_description ?? '',
              stateCode: opening.state_code ?? undefined,
              isExpanded: openings.length === 1,
              vonqPostingRequirements: mergedRequirements,
              vonqPostingRequirementsDefs,
            };

        return { ...acc, [opening.external_id]: base };
      },
      // Preserve any orphaned entries already in form state (e.g. an opening
      // removed via "Remove from posting" — UI still filters those out at render time).
      { ...existingJobDetails },
    );

    setValue('jobDetails', next, { shouldValidate: false });
  }, [openings, postingRequirementsData, channels, setValue, getValues]);

  /**
   * Trigger validation for base (common) fields whenever the server data
   * changes — intentionally excludes `vonqPostingRequirements` so that
   * posting-requirement errors don't surface until the user visits that section.
   * Does not run on `channels` changes to avoid re-validating on every keystroke.
   */
  useEffect(() => {
    if (openings.length === 0) return;

    void validateBaseFields();
  }, [openings, postingRequirementsData, validateBaseFields]);

  /** Filter the openings to remove the ones that are deleted from the form state */
  const filteredOpenings = useMemo(() => {
    return openings.filter(opening =>
      Object.keys(jobDetailsList ?? {}).includes(opening.external_id),
    );
  }, [openings, jobDetailsList]);

  return (
    <div className="flex w-full flex-col gap-3">
      {showRefreshBanner && (
        <RefreshBanner onClose={() => setShowRefreshBanner(false)} />
      )}

      {filteredOpenings.map(opening => (
        <JobCard
          key={opening.external_id}
          opening={opening}
          postingRequirements={postingRequirementsData ?? []}
          onRequestRefreshBanner={() => setShowRefreshBanner(true)}
        />
      ))}
    </div>
  );
}

export function JobDetails() {
  const { selectedOpeningIds } = useJobPostingContext();

  return (
    <Suspense
      fallback={
        <>
          {_range(selectedOpeningIds.length).map(index => (
            <SummaryView.Skeleton key={index} />
          ))}
        </>
      }
    >
      <JobList />
    </Suspense>
  );
}
